import React, { useMemo } from "react";

import { curry, filter, isEmpty, map, noop } from "lodash/fp";

import { Collapse } from "@mui/material";

import EditableContent from "@app/analytics/MeetingReport/components/EditableContent";
import { UpsellOverlay } from "@app/analytics/MeetingReport/components/UpsellOverlay";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import type {
  NoteItemOrChapter,
  NoteSearchResult,
  SessionChapterEdit,
} from "@app/analytics/MeetingReport/types";
import {
  chunkNoteBySearchResults,
  getChapterDescriptionSearchResultKey,
  getChapterTitleSearchResultKey,
} from "@app/analytics/MeetingReport/utils";
import type { SessionChapter } from "@app/common/types";

import ExpandableList from "@rui/ExpandableList";
import Stack, { Column } from "@rui/Stack";

import AnnotatedChapterDescription from "./AnnotatedChapterDescription";
import AnnotatedChapterHeader from "./AnnotatedChapterHeader";
import { ChapterHeading, ChapterTitle } from "./styles";
import NoteSubSection from "../NoteSubSection";
import NoteSubSectionWithAnnotations from "../NoteSubSectionWithAnnotations";

interface TopicsWithChaptersProps {
  chapters: SessionChapter[];
  onItemClick: (item: NoteItemOrChapter) => (() => void) | undefined;
  shouldUpsell: boolean;
  showChapterDescriptions: boolean;
}

const EMPTY_SEARCH_RESULTS: NoteSearchResult[] = [];
const EMPTY_SEARCH_RESULT_MAP = new Map<string, NoteSearchResult[]>();
const DEFAULT_MAX_CHAPTERS = 2;
const EXPANDABLE_LIST_BUTTON_PROPS = {
  viewMoreLabel: "View more",
  viewLessLabel: "View less",
  sx: { ml: 1 },
};

const EditableChapter = ({
  chapter,
  chapterSearchResults,
  onItemClick,
}: {
  chapter: SessionChapter;
  chapterSearchResults: Map<string, NoteSearchResult[]>;
  onItemClick: TopicsWithChaptersProps["onItemClick"];
}) => {
  const {
    isEditingDescription,
    isEditingTitle,
    editedTopics,
    onCloseEdit,
    onFocusItem,
    saveEdit,
  } = useEditStore((state) => ({
    isEditingDescription:
      state.notes.activeItem?.id === chapter.id &&
      state.notes.activeItem.subType === "description",
    isEditingTitle:
      state.notes.activeItem?.id === chapter.id &&
      state.notes.activeItem.subType === "title",
    editedTopics: state.notes.topics,
    onCloseEdit: state.exitItem,
    onFocusItem: state.startEditingItem,
    saveEdit: state.save,
  }));

  const chapterTitleSearchResults =
    chapterSearchResults.get(getChapterTitleSearchResultKey(chapter)) ??
    EMPTY_SEARCH_RESULTS;
  const chapterDescriptionSearchResults =
    chapterSearchResults.get(getChapterDescriptionSearchResultKey(chapter)) ??
    EMPTY_SEARCH_RESULTS;

  // topic edits are applied to the `topics` key of editedNotes, so when
  // showing just this chapter's topics, show the edited topics as they are
  // in the topics array, not the chapter's list of topics. but use the chapter's
  // list of topics to pick out the topics for that chapter by id
  const chapterTopicIds = map("id", chapter.topics);
  const chapterTopics = filter(
    ({ id }) => chapterTopicIds.includes(id),
    editedTopics
  );

  const handleToggleEditTitle = (editing: boolean) => {
    if (editing) {
      onFocusItem({ type: "chapter", item: chapter, subType: "title" });
    } else {
      onCloseEdit();
    }
  };

  const handleToggleEditDescription = (editing: boolean) => {
    if (editing) {
      onFocusItem({
        type: "chapter",
        item: chapter,
        subType: "description",
      });
    } else {
      onCloseEdit();
    }
  };

  const handleUpdateChapterTitle = (newTitle: string) => {
    if (newTitle === chapter.title) return;
    const edit: SessionChapterEdit = {
      type: "chapter",
      before: chapter,
      after: { ...chapter, title: newTitle },
    };
    saveEdit(edit);
  };

  const handleUpdateChapterDescription = (newDescription: string) => {
    if (newDescription === chapter.description) return;
    const edit: SessionChapterEdit = {
      type: "chapter",
      before: chapter,
      after: { ...chapter, description: newDescription },
    };
    saveEdit(edit);
  };

  const chapterTitleChunks = useMemo(() => {
    if (chapterTitleSearchResults.length) {
      return chunkNoteBySearchResults(chapter.title, chapterTitleSearchResults);
    }
    return chapter.title;
  }, [chapterTitleSearchResults, chapter.title]);

  const chapterDescriptionChunks = useMemo(() => {
    if (chapterDescriptionSearchResults.length) {
      return chunkNoteBySearchResults(
        chapter.description,
        chapterDescriptionSearchResults
      );
    }
    return chapter.description;
  }, [chapterDescriptionSearchResults, chapter.description]);

  return (
    <Column key={chapter.id}>
      <ChapterHeading>
        <ChapterTitle sx={{ p: 0, width: "100%" }}>
          <EditableContent
            editing={isEditingTitle}
            onSave={handleUpdateChapterTitle}
            onToggleEditing={handleToggleEditTitle}
            value={chapter.title}
            type="Chapter titles"
            contentChunks={chapterTitleChunks}
            sx={{ fontWeight: 500 }}
          />
        </ChapterTitle>
        <EditableContent
          editing={isEditingDescription}
          onSave={handleUpdateChapterDescription}
          onToggleEditing={handleToggleEditDescription}
          value={chapter.description}
          contentChunks={chapterDescriptionChunks}
          type="Chapter descriptions"
        />
      </ChapterHeading>
      {!isEmpty(chapterTopics) && (
        <Column paddingLeft={1}>
          <NoteSubSection
            items={chapterTopics}
            editedItems={chapterTopics}
            onItemClick={onItemClick}
            shouldUpsell={false}
            type="topic"
          />
        </Column>
      )}
    </Column>
  );
};

type ReadonlyChapterProps = Omit<
  TopicsWithChaptersProps,
  "chapters" | "shouldUpsell"
> & {
  chapter: SessionChapter;
  chapterSearchResults: Map<string, NoteSearchResult[]>;
  topicSearchResults: Map<string, NoteSearchResult[]>;
};

const ReadonlyChapter = ({
  chapter,
  onItemClick,
  chapterSearchResults,
  topicSearchResults,
  showChapterDescriptions,
}: ReadonlyChapterProps) => {
  const titleSearchResults = chapterSearchResults.get(
    getChapterTitleSearchResultKey(chapter)
  );
  const descriptionSearchResults = chapterSearchResults.get(
    getChapterDescriptionSearchResultKey(chapter)
  );

  return (
    <Column key={chapter.id}>
      <ChapterHeading
        onClick={showChapterDescriptions ? onItemClick(chapter) : undefined}
      >
        <ChapterTitle
          onClick={showChapterDescriptions ? undefined : onItemClick(chapter)}
        >
          <AnnotatedChapterHeader
            searchResults={titleSearchResults}
            timestamp={chapter.timestamp}
            title={chapter.title}
          />
        </ChapterTitle>
        <Collapse in={showChapterDescriptions}>
          <AnnotatedChapterDescription
            description={chapter.description}
            searchResults={descriptionSearchResults}
          />
        </Collapse>
      </ChapterHeading>
      {!isEmpty(chapter.topics) && (
        <Column ml={0.5}>
          <NoteSubSectionWithAnnotations
            items={chapter.topics}
            /** don't collapse topics when under a chapter regardless of how many there are */
            maxItems={chapter.topics.length}
            onItemClick={onItemClick}
            searchResults={topicSearchResults}
            type="topic"
            timestampsOnHover
          />
        </Column>
      )}
    </Column>
  );
};

const TopicsWithChapters = ({
  chapters,
  forceExpand,
  onItemClick,
  shouldUpsell,
  showChapterDescriptions,
}: {
  chapters: SessionChapter[];
  forceExpand: boolean;
  onItemClick: (item: NoteItemOrChapter) => (() => void) | undefined;
  shouldUpsell: boolean;
  showChapterDescriptions: boolean;
}) => {
  const { chapterSearchResults, topicSearchResults } = useSearchStore(
    (state) => ({
      chapterSearchResults: state.searchResultsByNoteTypeAndId.chapter,
      topicSearchResults: state.searchResultsByNoteTypeAndId.topic,
    })
  );

  const { editedChapters, editing } = useEditStore((state) => ({
    editedChapters: state.notes.chapters,
    editing:
      state.editingSection === "notes" && state.editAccessLevel === "full",
  }));

  if (shouldUpsell) {
    const chaptersToShow = chapters.slice(0, 1);
    return (
      <Column sx={{ position: "relative", pb: 1 }}>
        <UpsellOverlay>
          {chaptersToShow.map((chapter) => (
            <Stack key={chapter.id}>
              <ChapterHeading>
                <ChapterTitle>
                  <AnnotatedChapterHeader
                    timestamp={chapter.timestamp}
                    title={chapter.title}
                  />
                </ChapterTitle>
              </ChapterHeading>
              <Column ml={0.5}>
                <NoteSubSectionWithAnnotations
                  items={chapter.topics}
                  maxItems={chapter.topics.length}
                  onItemClick={curry(noop)}
                  searchResults={EMPTY_SEARCH_RESULT_MAP}
                  type="topic"
                />
              </Column>
            </Stack>
          ))}
        </UpsellOverlay>
      </Column>
    );
  }

  if (editing) {
    return (
      <Column>
        {editedChapters.map((chapter) => (
          <EditableChapter
            key={chapter.id}
            chapter={chapter}
            onItemClick={onItemClick}
            chapterSearchResults={chapterSearchResults}
          />
        ))}
      </Column>
    );
  }

  const hiddenChaptersHaveSearchResults = chapters
    .slice(DEFAULT_MAX_CHAPTERS)
    .some(
      (c) =>
        !isEmpty(chapterSearchResults.get(getChapterTitleSearchResultKey(c))) ||
        !isEmpty(
          chapterSearchResults.get(getChapterDescriptionSearchResultKey(c))
        )
    );

  // if any of the chapters below the fold have search results in their
  // titles or descriptions, force them to expand by upping the max to
  // the number of chapters
  const maxChaptersToShow =
    forceExpand || hiddenChaptersHaveSearchResults
      ? chapters.length
      : DEFAULT_MAX_CHAPTERS;

  return (
    <Column>
      <ExpandableList
        buttonProps={EXPANDABLE_LIST_BUTTON_PROPS}
        max={maxChaptersToShow}
        sx={chapters.length > maxChaptersToShow ? { mb: 1 } : undefined}
      >
        {chapters.map((chapter) => (
          <ReadonlyChapter
            key={chapter.id}
            chapter={chapter}
            onItemClick={onItemClick}
            chapterSearchResults={chapterSearchResults}
            topicSearchResults={topicSearchResults}
            showChapterDescriptions={showChapterDescriptions}
          />
        ))}
      </ExpandableList>
    </Column>
  );
};

export default TopicsWithChapters;
